/*
 * <reviews-stars-link> — routes a Reviews.io rating-snippet star row to that
 * product's reviews section instead of Reviews.io's own unbranded modal.
 *
 * Wrap the existing `.pdp-product__reviews` container element (keep its class —
 * every stylesheet selector for this row is class-based, none are `div.`-typed):
 *
 *   <reviews-stars-link class="pdp-product__reviews" data-href="/products/x?jg_cta=stars#reviews">
 *     <div class="ruk_rating_snippet ..." data-sku="..."></div>
 *   </reviews-stars-link>
 *
 * WHY THIS SETS `.onclick` INSTEAD OF addEventListener
 * ----------------------------------------------------
 * The rating-snippet widget (configured in layout/theme.liquid) attaches its own
 * popup handler in a loop guarded by `null == el.onclick`, and its `snippetPopup`
 * option DEFAULTS TO TRUE — so every hydrated `.ruk_rating_snippet` gets a modal
 * handler unless that property is already occupied. addEventListener leaves
 * `.onclick` null, so the widget would still attach its modal and a single click
 * would BOTH open the modal and navigate. Claiming the property is the deliberate
 * co-opt, not a lapse: rules/javascript.md anti-pattern 3 targets inline `onclick=`
 * attributes in markup, which this is not.
 *
 * The same trick applies to `role`. The widget skips its own `role="button"` when
 * a role is already present, so pre-setting `role="link"` keeps the announced
 * semantics honest — this row navigates, it does not act in place. The widget
 * still supplies `tabindex="0"` and an `aria-label` ("4.77 Stars - 44 Reviews").
 *
 * ORDERING is safe by construction: this file is deferred, so connectedCallback
 * runs at customElements.define() time — after parsing, before DOMContentLoaded.
 * layout/theme.liquid calls ratingSnippet() *on* DOMContentLoaded, and the
 * widget's MutationObserver re-init re-checks the same `null == el.onclick`
 * guard, so the claim holds across re-inits and theme-editor section reloads.
 * Verified live 2026-08-10: with the properties pre-claimed, a click injected 0
 * bytes into the modal and left it display:none, against 80,589 bytes unclaimed.
 *
 * Params (on the element):
 *   data-href — target ending in the reviews fragment. A value whose path matches
 *               the current page scrolls in-page instead of navigating away.
 */
if (!customElements.get('reviews-stars-link')) {
  class ReviewsStarsLink extends HTMLElement {
    connectedCallback() {
      this.snippet = this.querySelector('.ruk_rating_snippet');
      if (!this.snippet || !this.dataset.href) return;

      this.controller = new AbortController();

      // Claim both properties the widget guards on — see the header note.
      this.snippet.onclick = this.onActivate.bind(this);
      if (!this.snippet.getAttribute('role')) this.snippet.setAttribute('role', 'link');

      // The widget supplies tabindex="0", but a div with role="link" gets no
      // free Enter/Space activation. Verified: without this, Enter does nothing.
      this.snippet.addEventListener('keydown', this.onKeydown.bind(this), {
        signal: this.controller.signal,
      });

      /*
       * aria-label is the one attribute that CANNOT be pre-claimed. The widget
       * writes it unconditionally during hydration (title -> aria-label ->
       * innerHTML -> data-done), with no "already set" guard, so anything we
       * write in advance is overwritten. Instead, watch `data-done` — the
       * widget's own completion flag, written last — and restamp afterwards.
       *
       * attributeFilter is data-done ONLY: our aria-label write therefore
       * cannot re-trigger this observer, so there is no feedback loop, while a
       * later re-hydration (which re-sets data-done) does re-fire it.
       */
      if (this.dataset.labelSuffix) {
        this.observer = new MutationObserver(this.relabel.bind(this));
        this.observer.observe(this.snippet, { attributes: true, attributeFilter: ['data-done'] });
        this.relabel(); // hydration may already have completed
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
    }

    /*
     * "4.77 Stars - 44 Reviews" -> "4.77 Stars - 44 Reviews, read all reviews
     * for Baby Shark Bouncy". The widget's rating text is worth keeping — it is
     * the only place the rating is exposed to a screen reader, since every
     * injected star glyph and the count span are aria-hidden. The suffix adds
     * what a link's accessible name must carry and the widget's never did:
     * where it goes. Idempotent — re-running appends nothing twice.
     */
    relabel() {
      if (!this.isReady) return;
      const suffix = this.dataset.labelSuffix;
      const current = (this.snippet.getAttribute('aria-label') || '').trim();
      if (current.endsWith(suffix)) return;
      this.snippet.setAttribute('aria-label', current ? `${current}, ${suffix}` : suffix);
    }

    /*
     * Only act once the widget has actually rendered stars. Before hydration the
     * row is empty (and collapsed by the global `*:empty` reset); on a product
     * with no reviews the widget sets aria-hidden and strips tabindex, so it is
     * never a pointer or keyboard target. `data-done` is the widget's own signal.
     */
    get isReady() {
      return this.snippet.getAttribute('data-done') === 'true';
    }

    onKeydown(event) {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault(); // Space would otherwise scroll the page
      this.onActivate();
    }

    onActivate() {
      if (!this.isReady) return;
      const target = new URL(this.dataset.href, window.location.href);

      if (target.pathname !== window.location.pathname) {
        window.location.assign(target.href);
        return;
      }

      /*
       * Same page (the PDP's own buy-box row): hand the landing to
       * scroll-anchor.js, which owns the sticky-header offset and the
       * taller-than-viewport fallback. Setting the hash fires its hashchange
       * listener — but an unchanged hash fires nothing, so in that case call the
       * anchor's own centering entry point directly.
       */
      const anchor = document.getElementById(target.hash.slice(1));
      if (window.location.hash !== target.hash) {
        window.location.hash = target.hash;
      } else if (typeof anchor?.center === 'function') {
        anchor.center();
      } else {
        anchor?.scrollIntoView({ block: 'start', behavior: 'smooth' });
      }
    }
  }

  customElements.define('reviews-stars-link', ReviewsStarsLink);
}
